import * as React from "react";
import { Viewport, ViewSmall, ViewLarge } from "bernie-view";
import { inject } from "mobx-react";

import { UitkCarousel } from "uitk-react-carousel";
import { AnalyticsConfig } from "uitk-react-utils";

import { HotelCard } from "../components/HotelCard";
import { ViewAllHotelsButton } from "../components/ViewAllHotelsButton";
import { EstimatedPriceLabel } from "../components/EstimatedPriceLabel";
import { CardContent } from "../components/CardContent";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { Action, FlexTrackingInfo, sendDelayedTrackEvent } from "components/utility/analytics/FlexAnalyticsUtils";

import { ViewCarouselProps } from "../typings";
import { Hotel } from "typings/microserviceModels/hotels-flex-module";
import { AspectRatioEnum } from "stores/thumbor/ThumborStore";

@inject("thumbor", "analytics", "hotels")
export class ViewCarousel extends React.Component<ViewCarouselProps> {
  public componentDidMount(): void {
    const model = this.props.flexComponent.model;
    const hotels: Hotel[] = model.hotels;
    const hotelsOnMap: boolean = typeof model.showMarkersOnMap === "undefined" || model.showMarkersOnMap === true;

    if (this.props.hotels && hotelsOnMap && hotels && hotels.length > 0) {
      this.props.hotels.fetchMapHotelsById(hotels, this.props.context);
    }
  }

  // This should be removed after UITK Carousel is fixed. (https://jira.expedia.biz/browse/UDS-577)
  private safelyResizeImage(hotel: Hotel) {
    const image: string = hotel.hotelImageUrls && hotel.hotelImageUrls.y;

    return image && this.props.thumbor
      ? this.props.thumbor.safelyGenerateThumborizedImageUrl(
          image,
          340,
          150,
          this.props.context,
          AspectRatioEnum.DEFAULT,
          true,
          100
        )
      : image;
  }

  public render() {
    const flexComponent = this.props.flexComponent;
    const keyHelper = new ItemKeyHelper("hotels");
    const model = flexComponent && flexComponent.model;
    const { formatText } = this.props.l10n;
    const context = this.props.context;

    if (!flexComponent || !model || !model.hotels || model.hotels.length === 0) {
      return null;
    }

    const cardContent = (hotel: Hotel) => () => (
      <CardContent
        model={model}
        hotel={hotel}
        context={this.props.context}
        locale={this.props.context.locale}
        isViewCarousel={true}
      />
    );

    const hotelCardProps = (hotel: Hotel) => ({
      callToActionHotelName: model.callToActionHotelName,
      context,
      className: "all-r-margin-three",
      hotel,
      CardContent: cardContent(hotel),
      isMobile: true,
      showGuestRating: model.showGuestRating
    });

    // Since uitk-carousel is only client-side, we need to generate the thumbor images server-side here
    model.hotels.forEach(hotel => this.safelyResizeImage(hotel));

    // To be replaced by a more generic hotel card carousel
    const HotelCardCarousel = () => {
      const carouselProps = {
        itemsVisible: { lg: 1, md: 1, sm: 1 },
        pageBy: 1,
        peekWidth: 24,
        buttonText: {
          nextButton: formatText("carousel.item.next"),
          prevButton: formatText("carousel.item.prev")
        }
      };

      const hotelAnalyticsId = "TG.LP.Dest.Hotels";
      const scrolledPastSecondHotel = (moduleName: string) => {
        const hotelAnalyticsModuleName = `${hotelAnalyticsId}.Hotels.Carousel.itemViewed`;
        const firstHotelModuleName = `${hotelAnalyticsModuleName}.0`;
        const secondHotelModuleName = `${hotelAnalyticsModuleName}.1`;

        return moduleName !== firstHotelModuleName && moduleName !== secondHotelModuleName;
      };

      const analytics: AnalyticsConfig = {
        id: hotelAnalyticsId,
        callback: (id, description) => {
          const flexTrackingInfo: FlexTrackingInfo = {
            moduleName: id,
            action: Action.SCROLL,
            linkName: description
          };
          if (scrolledPastSecondHotel(id)) {
            sendDelayedTrackEvent(flexTrackingInfo, this.props.analytics);
          }
        }
      };

      return (
        <>
          <UitkCarousel analytics={analytics} carouselName={"Hotels"} {...carouselProps} headline={model.title}>
            {model.hotels.map((hotel, hotelIndex) => (
              <HotelCard
                {...hotelCardProps(hotel)}
                key={keyHelper.next()}
                className="uitk-carousel-item"
                index={hotelIndex + 1}
              />
            ))}
          </UitkCarousel>
          <EstimatedPriceLabel className="all-t-padding-two" />
        </>
      );
    };

    return (
      <Viewport>
        <ViewSmall>
          <div
            className="Hotels Hotels_ViewCarousel uitk-cell all-b-margin-six"
            id={flexComponent.id}
            data-fm={flexComponent.fmId}
          >
            <HotelCardCarousel />
            <ViewAllHotelsButton model={model} />
          </div>
        </ViewSmall>
        <ViewLarge>
          <div
            className="Hotels uitk-cell all-cell-1-1 all-b-margin-six"
            id={flexComponent.id}
            data-fm={flexComponent.fmId}
          >
            <h3 className="uitk-type-heading-600">{model.title}</h3>
            <EstimatedPriceLabel />
            <div className="uitk-grid all-cell-1-3 all-x-gutter-three">
              {model.hotels.map((hotel, hotelIndex) => (
                <div className="HotelsCard uitk-cell all-b-padding-three all-b-margin-three" key={keyHelper.next()}>
                  <HotelCard {...hotelCardProps(hotel)} index={hotelIndex + 1} />
                </div>
              ))}
            </div>
            <ViewAllHotelsButton model={model} />
          </div>
        </ViewLarge>
      </Viewport>
    );
  }
}

export default ViewCarousel;
